import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    IconButton,
    Typography,
    RadioGroup,
    Radio,
    FormControlLabel,
    FormLabel,
} from '@material-ui/core';
import { Autocomplete, Chip } from '@mui/material';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles((theme) => ({
    fieldRow: {
        display: 'flex',
        gap: theme.spacing(2),
        marginBottom: theme.spacing(2),
        alignItems: 'center',
        flexWrap: 'wrap',
    },
    modeRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
    formControl: {
        minWidth: 200,
    },
    table: {
        marginTop: theme.spacing(2),
    },
    narrowCell: {
        width: 120,
    },
    addRowBtn: {
        marginTop: theme.spacing(1),
    },
    numericField: {
        width: 110,
    },
    zipField: {
        width: 120,
    },
    municipalityField: {
        minWidth: 260,
    },
    overlapError: {
        color: theme.palette.error.main,
        marginTop: theme.spacing(1),
    },
}));

const ZIPCODE_PATTERN = /^\d{5}$/;

const buildAutoName = (providerId, productId, providers, products) => {
    const provider = (providers || []).find((p) => p.clusterId === providerId);
    const product = (products || []).find((p) => p.clusterId === productId);
    if (!provider && !product) return '';
    const parts = [provider && provider.name, product && product.type].filter(Boolean);
    return parts.join(' \u2013 ');
};

export default function CargoPurchaseGroupEditor({
    open,
    group,
    providers,
    products,
    providerProducts,
    existingGroups,
    onSave,
    onCancel,
}) {
    const classes = useStyles();
    const { t } = useTranslation();

    const emptyGroup = {
        clusterId: 0,
        name: '',
        cargoProviderId: '',
        cargoProductId: '',
        costRows: [],
    };

    const [editing, setEditing] = useState(emptyGroup);
    const [autoName, setAutoName] = useState(true);
    // pricingMode ('zip' | 'nth' | 'municipality' | null) enforces that a group prices either by
    // zipcode range, item count (nthPrice), or a list of municipalities per row – never a mix.
    // It is persisted on the group as `pricingMode`.
    const [pricingMode, setPricingMode] = useState(null);
    const municipalities = useSelector((s) => (s.postaldata || {}).municipalities);

    const derivePricingMode = (rows) => {
        if (!rows || rows.length === 0) return null;
        if (rows.some((r) => r.municipalityCodes && r.municipalityCodes.length > 0)) return 'municipality';
        return rows.some((r) => Number(r.nthPrice) > 1) ? 'nth' : 'zip';
    };

    const normalizeRowsForMode = (rows, mode) => {
        if (!mode) return rows;
        return rows.map((r) => {
            if (mode === 'zip') return { ...r, nthPrice: 1, municipalityCodes: [] };
            if (mode === 'nth') return { ...r, zipcodeRangeStart: '', zipcodeRangeEnd: '', municipalityCodes: [] };
            return { ...r, zipcodeRangeStart: '', zipcodeRangeEnd: '', nthPrice: 1 };
        });
    };

    useEffect(() => {
        if (open) {
            const isNew = !group || group.clusterId === 0;
            const productId = (group && group.costRows && group.costRows[0] && group.costRows[0].cargoProductId) || '';
            const rows = group && group.costRows ? [...group.costRows] : [];
            // Prefer the persisted pricingMode from the backend; fall back to deriving it
            // from existing rows for legacy data saved before this field existed.
            const derivedMode = (group && group.pricingMode) || derivePricingMode(rows);
            setEditing(
                group
                    ? { ...group, cargoProductId: productId, costRows: normalizeRowsForMode(rows, derivedMode) }
                    : emptyGroup
            );
            setPricingMode(derivedMode);
            setAutoName(isNew);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, group]);

    // Once at least one row exists (new group with a row added, or any existing group,
    // which always has rows), the pricing mode is locked in and can no longer be switched.
    const isPricingModeLocked = editing.costRows.length > 0;

    const handlePricingModeChange = (mode) => {
        if (isPricingModeLocked) return;
        setPricingMode(mode);
        setEditing((prev) => ({ ...prev, costRows: normalizeRowsForMode(prev.costRows, mode) }));
    };

    const handleFieldChange = (field, value) => {
        setEditing((prev) => {
            const updated = { ...prev, [field]: value };
            if (field === 'cargoProviderId') {
                // The product list is filtered to the newly selected provider's linked products
                // (see availableProducts below); drop a product choice that is no longer valid.
                const stillAvailable = (providerProducts || []).some(
                    (pp) => pp.cargoProviderId === value && pp.cargoProductId === prev.cargoProductId
                );
                if (!stillAvailable) {
                    updated.cargoProductId = '';
                    updated.costRows = prev.costRows.map((r) => ({ ...r, cargoProductId: 0 }));
                }
            }
            if (field === 'cargoProductId') {
                updated.costRows = prev.costRows.map((r) => ({ ...r, cargoProductId: value || 0 }));
            }
            if ((field === 'cargoProviderId' || field === 'cargoProductId') && autoName) {
                const providerId = field === 'cargoProviderId' ? value : prev.cargoProviderId;
                const productId = field === 'cargoProductId' ? value : prev.cargoProductId;
                updated.name = buildAutoName(providerId, productId, providers, products);
            }
            return updated;
        });
    };

    const handleNameChange = (value) => {
        setAutoName(false);
        setEditing((prev) => ({ ...prev, name: value }));
    };

    const handleRowChange = (index, field, value) => {
        setEditing((prev) => {
            const rows = [...prev.costRows];
            rows[index] = { ...rows[index], [field]: value };
            return { ...prev, costRows: rows };
        });
    };

    const addRow = () => {
        setEditing((prev) => {
            // In nth pricing mode, auto-assign the next free Nro value so the user doesn't
            // have to type it manually for every new row.
            const nextNthPrice =
                pricingMode === 'nth' ? Math.max(0, ...prev.costRows.map((r) => Number(r.nthPrice) || 0)) + 1 : 1;
            return {
                ...prev,
                costRows: [
                    ...prev.costRows,
                    {
                        clusterId: 0,
                        cargoProductId: prev.cargoProductId || 0,
                        priceWithoutVat: 0,
                        zipcodeRangeStart: '',
                        zipcodeRangeEnd: '',
                        nthPrice: nextNthPrice,
                        municipalityCodes: [],
                    },
                ],
            };
        });
    };

    const deleteRow = (index) => {
        setEditing((prev) => {
            const rows = prev.costRows.filter((_, i) => i !== index);
            return { ...prev, costRows: rows };
        });
    };

    // All products are always shown in the product selector so the user can see the full
    // catalog, but entries not linked to the selected provider (per the explicit provider-product
    // linking managed in the "Rahtipalvelut" tab, cargo_provider_product) are rendered disabled -
    // pricing should only be defined for combinations that are actually linked there. The
    // already-selected product (e.g. on an existing group) stays selectable even if unlinked, so
    // opening it for editing never silently clears a previously saved selection.
    const linkedProductIds = useMemo(
        () =>
            new Set(
                (providerProducts || [])
                    .filter((pp) => pp.cargoProviderId === editing.cargoProviderId)
                    .map((pp) => pp.cargoProductId)
            ),
        [editing.cargoProviderId, providerProducts]
    );

    const isProductDisabled = (productId) =>
        !!editing.cargoProviderId && !linkedProductIds.has(productId) && productId !== editing.cargoProductId;

    // Shown whenever the selected provider is not linked to every available product, hinting
    // that additional provider-product combinations may need to be configured on the
    // "Rahtipalvelut" tab before pricing can be defined for them.
    const missingProductLinks = !!editing.cargoProviderId && linkedProductIds.size < (products || []).length;

    const overlappingRows = useMemo(() => {
        const result = new Set();
        if (pricingMode !== 'zip') return result;
        const groups = {};
        editing.costRows.forEach((row, index) => {
            const key = `${row.cargoProductId}_${row.nthPrice}`;
            if (!groups[key]) groups[key] = [];
            groups[key].push({ ...row, index });
        });
        Object.values(groups).forEach((group) => {
            for (let i = 0; i < group.length; i++) {
                for (let j = i + 1; j < group.length; j++) {
                    const a = group[i];
                    const b = group[j];
                    const aStart = a.zipcodeRangeStart || '00000';
                    const aEnd = a.zipcodeRangeEnd || '99999';
                    const bStart = b.zipcodeRangeStart || '00000';
                    const bEnd = b.zipcodeRangeEnd || '99999';
                    if (aStart <= bEnd && aEnd >= bStart) {
                        result.add(a.index);
                        result.add(b.index);
                    }
                }
            }
        });
        return result;
    }, [editing.costRows, pricingMode]);

    const invalidFormatRows = useMemo(() => {
        const result = new Set();
        if (pricingMode !== 'zip') return result;
        editing.costRows.forEach((row, index) => {
            const start = row.zipcodeRangeStart || '';
            const end = row.zipcodeRangeEnd || '';
            if ((start && !ZIPCODE_PATTERN.test(start)) || (end && !ZIPCODE_PATTERN.test(end))) {
                result.add(index);
            }
        });
        return result;
    }, [editing.costRows, pricingMode]);

    const invalidRangeRows = useMemo(() => {
        const result = new Set();
        if (pricingMode !== 'zip') return result;
        editing.costRows.forEach((row, index) => {
            const start = row.zipcodeRangeStart || '';
            const end = row.zipcodeRangeEnd || '';
            if (start && end && start > end) {
                result.add(index);
            }
        });
        return result;
    }, [editing.costRows, pricingMode]);

    const duplicateMunicipalityRows = useMemo(() => {
        const result = new Set();
        if (pricingMode !== 'municipality') return result;
        const codeToRows = {};
        editing.costRows.forEach((row, index) => {
            (row.municipalityCodes || []).forEach((code) => {
                if (!codeToRows[code]) codeToRows[code] = [];
                codeToRows[code].push(index);
            });
        });
        Object.values(codeToRows).forEach((indices) => {
            if (indices.length > 1) indices.forEach((i) => result.add(i));
        });
        return result;
    }, [editing.costRows, pricingMode]);

    const emptyMunicipalityRows = useMemo(() => {
        const result = new Set();
        if (pricingMode !== 'municipality') return result;
        editing.costRows.forEach((row, index) => {
            if (!row.municipalityCodes || row.municipalityCodes.length === 0) result.add(index);
        });
        return result;
    }, [editing.costRows, pricingMode]);

    // Options for a row's municipality picker exclude municipalities already used by other
    // rows in the group, preventing duplicate municipalities from being selectable at all.
    const municipalityOptionsFor = (index) => {
        const ownCodes = new Set((editing.costRows[index] && editing.costRows[index].municipalityCodes) || []);
        const usedElsewhere = new Set();
        editing.costRows.forEach((r, i) => {
            if (i === index) return;
            (r.municipalityCodes || []).forEach((c) => usedElsewhere.add(c));
        });
        return (municipalities || []).filter((m) => ownCodes.has(m.municipalityCode) || !usedElsewhere.has(m.municipalityCode));
    };

    const municipalityValuesFor = (row) => {
        const codes = row.municipalityCodes || [];
        return codes.map(
            (code) =>
                (municipalities || []).find((m) => m.municipalityCode === code) || {
                    municipalityCode: code,
                    municipalityName: code,
                }
        );
    };

    const sortedRows = useMemo(() => {
        return editing.costRows
            .map((row, index) => ({ row, index }))
            .sort((a, b) => {
                const aStart = a.row.zipcodeRangeStart || '00000';
                const bStart = b.row.zipcodeRangeStart || '00000';
                if (aStart !== bStart) return aStart < bStart ? -1 : 1;
                const aEnd = a.row.zipcodeRangeEnd || '99999';
                const bEnd = b.row.zipcodeRangeEnd || '99999';
                if (aEnd !== bEnd) return aEnd < bEnd ? -1 : 1;
                return a.index - b.index;
            });
    }, [editing.costRows]);

    // Zipcode-range pricing and nth-item pricing are mutually exclusive within a group,
    // enforced structurally: fields for the inactive mode are disabled, not merely validated.

    const isDuplicateCombination = !!(
        editing.cargoProviderId &&
        editing.cargoProductId &&
        (existingGroups || []).some(
            (g) =>
                g.clusterId !== editing.clusterId &&
                g.cargoProviderId === editing.cargoProviderId &&
                g.costRows &&
                g.costRows[0] &&
                g.costRows[0].cargoProductId === editing.cargoProductId
        )
    );

    const isSaveDisabled =
        !editing.name ||
        !editing.cargoProviderId ||
        !editing.cargoProductId ||
        overlappingRows.size > 0 ||
        invalidFormatRows.size > 0 ||
        invalidRangeRows.size > 0 ||
        duplicateMunicipalityRows.size > 0 ||
        emptyMunicipalityRows.size > 0 ||
        isDuplicateCombination;

    return (
        <Dialog open={open} onClose={onCancel} maxWidth="md" fullWidth>
            <DialogTitle>
                {editing.clusterId > 0 ? t('cargo.editPurchaseGroup') : t('cargo.newPurchaseGroup')}
            </DialogTitle>
            <DialogContent>
                {missingProductLinks && (
                    <Typography variant="body2" color="error" gutterBottom>
                        {t('cargo.providerProductLinkMissingHint')}
                    </Typography>
                )}
                <div className={classes.fieldRow}>
                    <FormControl size="small" className={classes.formControl} required>
                        <InputLabel>{t('cargo.provider')}</InputLabel>
                        <Select
                            value={editing.cargoProviderId || ''}
                            onChange={(e) => handleFieldChange('cargoProviderId', e.target.value)}>
                            {(providers || []).map((p) => (
                                <MenuItem key={p.clusterId} value={p.clusterId}>
                                    {p.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl size="small" className={classes.formControl} required>
                        <InputLabel>{t('cargo.product')}</InputLabel>
                        <Select
                            value={editing.cargoProductId || ''}
                            onChange={(e) => handleFieldChange('cargoProductId', e.target.value)}>
                            {(products || []).map((p) => (
                                <MenuItem key={p.clusterId} value={p.clusterId} disabled={isProductDisabled(p.clusterId)}>
                                    {p.type}
                                    {p.description ? ` – ${p.description}` : ''}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        label={t('general.name')}
                        value={editing.name}
                        onChange={(e) => handleNameChange(e.target.value)}
                        size="small"
                        style={{ minWidth: 240 }}
                    />
                </div>

                <div className={classes.modeRow}>
                    <FormControl component="fieldset" required disabled={isPricingModeLocked}>
                        <FormLabel component="legend">{t('cargo.pricingMode')}</FormLabel>
                        <RadioGroup
                            row
                            value={pricingMode || ''}
                            onChange={(e) => handlePricingModeChange(e.target.value)}>
                            <FormControlLabel
                                value="zip"
                                control={<Radio size="small" disabled={isPricingModeLocked} />}
                                label={t('cargo.pricingModeZip')}
                            />
                            <FormControlLabel
                                value="nth"
                                control={<Radio size="small" disabled={isPricingModeLocked} />}
                                label={t('cargo.pricingModeNth')}
                            />
                            <FormControlLabel
                                value="municipality"
                                control={<Radio size="small" disabled={isPricingModeLocked} />}
                                label={t('cargo.pricingModeMunicipality')}
                            />
                        </RadioGroup>
                        {isPricingModeLocked && (
                            <Typography variant="caption" color="textSecondary">
                                {t('cargo.pricingModeLockedHint')}
                            </Typography>
                        )}
                    </FormControl>
                </div>

                <Table className={classes.table} size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell className={classes.narrowCell}>{t('cargo.priceWithoutVat')}</TableCell>
                            {pricingMode === 'zip' && (
                                <>
                                    <TableCell className={classes.narrowCell}>{t('cargo.zipcodeRangeStart')}</TableCell>
                                    <TableCell className={classes.narrowCell}>{t('cargo.zipcodeRangeEnd')}</TableCell>
                                </>
                            )}
                            {pricingMode === 'nth' && (
                                <TableCell className={classes.narrowCell}>{t('cargo.nthPrice')}</TableCell>
                            )}
                            {pricingMode === 'municipality' && <TableCell>{t('cargo.municipalities')}</TableCell>}
                            <TableCell />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sortedRows.map(({ row, index }) => (
                            <TableRow key={index}>
                                <TableCell>
                                    <TextField
                                        value={row.priceWithoutVat}
                                        onChange={(e) => handleRowChange(index, 'priceWithoutVat', e.target.value)}
                                       
                                        size="small"
                                        type="number"
                                        className={classes.numericField}
                                    />
                                </TableCell>
                                {pricingMode === 'zip' && (
                                    <>
                                        <TableCell>
                                            <TextField
                                                value={row.zipcodeRangeStart}
                                                onChange={(e) => handleRowChange(index, 'zipcodeRangeStart', e.target.value)}
                                                size="small"
                                                className={classes.zipField}
                                                error={
                                                    overlappingRows.has(index) ||
                                                    invalidFormatRows.has(index) ||
                                                    invalidRangeRows.has(index)
                                                }
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <TextField
                                                value={row.zipcodeRangeEnd}
                                                onChange={(e) => handleRowChange(index, 'zipcodeRangeEnd', e.target.value)}
                                                size="small"
                                                className={classes.zipField}
                                                error={
                                                    overlappingRows.has(index) ||
                                                    invalidFormatRows.has(index) ||
                                                    invalidRangeRows.has(index)
                                                }
                                            />
                                        </TableCell>
                                    </>
                                )}
                                {pricingMode === 'nth' && (
                                    <TableCell>
                                        <TextField
                                            value={row.nthPrice}
                                            onChange={(e) => handleRowChange(index, 'nthPrice', e.target.value)}
                                            size="small"
                                            type="number"
                                            className={classes.numericField}
                                        />
                                    </TableCell>
                                )}
                                {pricingMode === 'municipality' && (
                                    <TableCell>
                                        <Autocomplete
                                            multiple
                                            size="small"
                                            options={municipalityOptionsFor(index)}
                                            value={municipalityValuesFor(row)}
                                            getOptionLabel={(option) => option.municipalityName || option.municipalityCode || ''}
                                            isOptionEqualToValue={(option, value) => option.municipalityCode === value.municipalityCode}
                                            onChange={(e, newValue) =>
                                                handleRowChange(
                                                    index,
                                                    'municipalityCodes',
                                                    newValue.map((m) => m.municipalityCode)
                                                )
                                            }
                                            renderTags={(tagValue, getTagProps) =>
                                                tagValue.map((option, tagIndex) => (
                                                    <Chip
                                                        size="small"
                                                        label={option.municipalityName || option.municipalityCode}
                                                        {...getTagProps({ index: tagIndex })}
                                                        key={option.municipalityCode}
                                                    />
                                                ))
                                            }
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    size="small"
                                                    error={duplicateMunicipalityRows.has(index) || emptyMunicipalityRows.has(index)}
                                                />
                                            )}
                                            className={classes.municipalityField}
                                        />
                                    </TableCell>
                                )}
                                <TableCell>
                                    <IconButton size="small" onClick={() => deleteRow(index)}>
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>

                <Button
                    className={classes.addRowBtn}
                    startIcon={<AddIcon />}
                    onClick={addRow}
                    size="small"
                    disabled={!editing.cargoProductId || !pricingMode}>
                    {t('cargo.addCostRow')}
                </Button>
                {overlappingRows.size > 0 && (
                    <Typography variant="body2" className={classes.overlapError}>
                        {t('cargo.zipcodeOverlapError')}
                    </Typography>
                )}
                {invalidFormatRows.size > 0 && (
                    <Typography variant="body2" className={classes.overlapError}>
                        {t('cargo.zipcodeFormatError')}
                    </Typography>
                )}
                {invalidRangeRows.size > 0 && (
                    <Typography variant="body2" className={classes.overlapError}>
                        {t('cargo.zipcodeRangeInvalidError')}
                    </Typography>
                )}
                {duplicateMunicipalityRows.size > 0 && (
                    <Typography variant="body2" className={classes.overlapError}>
                        {t('cargo.municipalityDuplicateError')}
                    </Typography>
                )}
                {emptyMunicipalityRows.size > 0 && (
                    <Typography variant="body2" className={classes.overlapError}>
                        {t('cargo.municipalityRequiredError')}
                    </Typography>
                )}
                {isDuplicateCombination && (
                    <Typography variant="body2" className={classes.overlapError}>
                        {t('cargo.duplicateCombinationError')}
                    </Typography>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onCancel} variant="contained" color="default">
                    {t('buttons.cancel')}
                </Button>
                <Button
                    onClick={() => onSave({ ...editing, pricingMode })}
                    variant="contained"
                    color="primary"
                    disabled={isSaveDisabled}>
                    {t('buttons.save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

